import {
  DEFAULT_ORG_REGISTRATION_TAB,
  EMPTY_ORG_REGISTRATION_FILTERS,
  ORG_REGISTRATION_FILTER_PARAM_KEYS,
  ORG_REGISTRATION_TABS,
} from '../constants'
import type { OrgRegistrationFilters, OrgRegistrationTab } from '../types'

const isTab = (value: string): value is OrgRegistrationTab =>
  (ORG_REGISTRATION_TABS as readonly string[]).includes(value)

/** How a boolean filter reads in the URL when on; absent means off. */
const FLAG_ON = '1'

/**
 * Filters → query-string pairs. Unset filters, the default tab, and switches
 * left off are left out entirely, so an unfiltered list keeps a clean URL and
 * a shared link carries only what was actually chosen.
 */
export const orgRegistrationFiltersToParams = (
  filters: OrgRegistrationFilters
): Record<string, string> => {
  const keys = ORG_REGISTRATION_FILTER_PARAM_KEYS
  const params: Record<string, string> = {}
  const setText = (key: string, value: string) => {
    const trimmed = value.trim()
    if (trimmed) {
      params[key] = trimmed
    }
  }

  if (filters.status !== DEFAULT_ORG_REGISTRATION_TAB) {
    params[keys.status] = filters.status
  }
  setText(keys.search, filters.search)
  setText(keys.sportId, filters.sportId)
  setText(keys.genericSportId, filters.genericSportId)
  setText(keys.venueId, filters.venueId)
  if (filters.showChildOrganizations) {
    params[keys.showChildOrganizations] = FLAG_ON
  }
  return params
}

/**
 * Query string → filters, filling anything absent from the defaults.
 *
 * Tolerant on purpose: an unknown status falls back to the default tab and an
 * unknown param is ignored, so a hand-edited or truncated link still opens a
 * usable list rather than an error.
 */
export const orgRegistrationFiltersFromParams = (
  search: string
): OrgRegistrationFilters => {
  const query = new URLSearchParams(search)
  const read = (field: keyof OrgRegistrationFilters): string =>
    (query.get(ORG_REGISTRATION_FILTER_PARAM_KEYS[field]) ?? '').trim()

  const status = read('status')
  const sportId = read('sportId')
  const genericSportId = read('genericSportId')

  return {
    ...EMPTY_ORG_REGISTRATION_FILTERS,
    status: isTab(status) ? status : DEFAULT_ORG_REGISTRATION_TAB,
    search: read('search'),
    // One sport at a time: a generic pick wins over a stale full-stats one.
    sportId: genericSportId ? '' : sportId,
    genericSportId,
    venueId: read('venueId'),
    showChildOrganizations: read('showChildOrganizations') === FLAG_ON,
  }
}

/**
 * Merges a change into the current URL's query string, keeping any params the
 * filters don't own (another feature's, a tracking tag) untouched.
 */
export const mergeOrgRegistrationFiltersIntoSearch = (
  currentSearch: string,
  filters: OrgRegistrationFilters
): string => {
  const query = new URLSearchParams(currentSearch)
  Object.values(ORG_REGISTRATION_FILTER_PARAM_KEYS).forEach((key) =>
    query.delete(key)
  )
  Object.entries(orgRegistrationFiltersToParams(filters)).forEach(
    ([key, value]) => query.set(key, value)
  )
  return query.toString()
}
